import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import {
  TrendingUp, BarChart2, Repeat2, ArrowLeftRight,
  Trophy, RefreshCw, Loader, ClipboardList,
  Coins, Flame, Star, AlertTriangle, ArrowUp, ArrowDown,
  Minus, BookOpen, Zap,
} from "lucide-react";
import levelsData from "../data/levels.json";
import "../styles/statisticsPage.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

// ── Tier helpers (mirrors rankedGame.js) ─────────────────────────────────────
function eloToTier(elo) {
  if (!elo || elo <= 0) return 1;
  return Math.max(1, Math.min(30, Math.ceil(elo / 100)));
}
function tierInfo(tier) {
  const t = levelsData.tiers[String(tier)] || levelsData.tiers["1"];
  return { name: t.name, color: t.color };
}
function eloDelta(history) {
  if (!history || history.length < 2) return null;
  const last  = history[history.length - 1];
  const prev  = history[history.length - 2];
  const eloField = last.elo ?? last.elo_after_match;
  const prevField = prev.elo ?? prev.elo_after_match;
  return eloField - prevField;
}

// ── Date formatter for chart X-axis ──────────────────────────────────────────
function fmtChartDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// ── Mode chart config ─────────────────────────────────────────────────────────
const CHART_MODES = [
  { key: "ranked",   label: "Ranked",    color: "#6366f1", eloKey: "elo_after_match", dateKey: "played_at" },
  { key: "vocab",    label: "Vocab",     color: "#10b981", eloKey: "elo",             dateKey: "created_at" },
  { key: "synonyms", label: "Synonyms",  color: "#14b8a6", eloKey: "elo",             dateKey: "created_at" },
  { key: "antonyms", label: "Antonyms",  color: "#f97316", eloKey: "elo",             dateKey: "created_at" },
];

const GAME_LABELS = {
  comprehension:      "Comprehension",
  mcq:                "MCQ Master",
  ranked:             "Ranked",
  book_comprehension: "Book Comprehension",
  vocab_definitions:  "Vocab: Definitions",
  vocab_synonyms:     "Vocab: Synonyms",
  vocab_antonyms:     "Vocab: Antonyms",
  vocab_mixed:        "Vocab: Mixed",
};
function gameLabel(type) {
  return GAME_LABELS[type] || type.replace(/[-_]/g, " ");
}

// ── XP level bar ─────────────────────────────────────────────────────────────
function XpBar({ level, xp, progressPct, xpToNext }) {
  return (
    <div className="sp2-xp-wrap">
      <div className="sp2-xp-header">
        <span className="sp2-xp-label">Level {level}</span>
        <span className="sp2-xp-sub">{xp} / {xp + xpToNext} XP</span>
      </div>
      <div className="sp2-xp-track">
        <div className="sp2-xp-fill" style={{ width: `${Math.min(100, progressPct || 0)}%` }} />
      </div>
    </div>
  );
}

// ── Delta badge ───────────────────────────────────────────────────────────────
function DeltaBadge({ delta }) {
  if (delta === null || delta === undefined) return null;
  if (delta > 0)  return <span className="sp2-delta sp2-delta--up"><ArrowUp size={11} />{delta}</span>;
  if (delta < 0)  return <span className="sp2-delta sp2-delta--down"><ArrowDown size={11} />{Math.abs(delta)}</span>;
  return <span className="sp2-delta sp2-delta--flat"><Minus size={11} />0</span>;
}

// ── Custom chart tooltip ──────────────────────────────────────────────────────
function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { elo, date, tier } = payload[0].payload;
  const { name, color } = tierInfo(tier);
  return (
    <div className="sp2-tooltip">
      <p className="sp2-tooltip-elo">{elo} ELO</p>
      <p className="sp2-tooltip-tier" style={{ color }}>{name}</p>
      {date && <p className="sp2-tooltip-date">{date}</p>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function StatisticsPage() {
  const userId = parseInt(localStorage.getItem("userId"), 10);

  // ELO current
  const [rankedElo,   setRankedElo]   = useState(null);
  const [vocabElo,    setVocabElo]    = useState(null);
  const [synonymsElo, setSynonymsElo] = useState(null);
  const [antonymsElo, setAntonymsElo] = useState(null);

  // ELO history (for chart + delta)
  const [rankedHist,   setRankedHist]   = useState([]);
  const [vocabHist,    setVocabHist]    = useState([]);
  const [synonymsHist, setSynonymsHist] = useState([]);
  const [antonymsHist, setAntonymsHist] = useState([]);

  // Other data
  const [modeAverages, setModeAverages] = useState([]);
  const [xpData,       setXpData]       = useState(null);
  const [coins,        setCoins]        = useState(null);
  const [streak,       setStreak]       = useState(null);
  const [weakWords,    setWeakWords]     = useState([]);
  const [vocabMap,     setVocabMap]      = useState(null);

  // UI state
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [chartMode,     setChartMode]     = useState("ranked");
  const [chartLimit,    setChartLimit]    = useState(50);

  // ── Fetchers ────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!userId) { setError("Not logged in"); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      await Promise.all([
        apiFetch(`/api/ranked-elo?userId=${userId}`)
          .then(r => r.ok ? r.json() : null).then(d => setRankedElo(d)),
        apiFetch(`/api/vocab/current-elo?userId=${userId}`)
          .then(r => r.ok ? r.json() : null).then(d => setVocabElo(d)),
        apiFetch(`/api/synonyms/current-elo?userId=${userId}`)
          .then(r => r.ok ? r.json() : null).then(d => setSynonymsElo(d)),
        apiFetch(`/api/antonyms/current-elo?userId=${userId}`)
          .then(r => r.ok ? r.json() : null).then(d => setAntonymsElo(d)),

        apiFetch(`/api/ranked-elo/history?userId=${userId}&order=asc&limit=200`)
          .then(r => r.ok ? r.json() : []).then(d => setRankedHist(Array.isArray(d) ? d : [])),
        apiFetch(`/api/vocab/elo-history?userId=${userId}`)
          .then(r => r.ok ? r.json() : {}).then(d => setVocabHist(d?.history || [])),
        apiFetch(`/api/synonyms/elo-history?userId=${userId}`)
          .then(r => r.ok ? r.json() : {}).then(d => setSynonymsHist(d?.history || [])),
        apiFetch(`/api/antonyms/elo-history?userId=${userId}`)
          .then(r => r.ok ? r.json() : {}).then(d => setAntonymsHist(d?.history || [])),

        apiFetch(`/api/modes/averages?userId=${userId}`)
          .then(r => r.ok ? r.json() : []).then(d => setModeAverages(Array.isArray(d) ? d : [])),
        apiFetch(`/api/resources/xp/${userId}`)
          .then(r => r.ok ? r.json() : null).then(d => setXpData(d)),
        apiFetch(`/api/resources/${userId}`)
          .then(r => r.ok ? r.json() : null).then(d => setCoins(d?.coins ?? null)),
        apiFetch(`/api/calendar/${userId}`)
          .then(r => r.ok ? r.json() : null).then(d => setStreak(d?.streak ?? null)),
        apiFetch(`/api/vocab-test/weak-words?userId=${userId}&limit=12`)
          .then(r => r.ok ? r.json() : []).then(d => setWeakWords(Array.isArray(d) ? d : [])),
      ]);
    } catch (e) {
      console.error(e);
      setError("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Resolve weak-word IDs → word text once vocab arrives
  useEffect(() => {
    if (!weakWords.length || vocabMap) return;
    apiFetch(`/api/vocabulary`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        const words = Array.isArray(d) ? d : d.words || [];
        setVocabMap(new Map(words.map(w => [w.id, w])));
      })
      .catch(() => {});
  }, [weakWords, vocabMap]);

  // ── Chart data builder ───────────────────────────────────────────────────────
  const historyFor = { ranked: rankedHist, vocab: vocabHist, synonyms: synonymsHist, antonyms: antonymsHist };
  const mode = CHART_MODES.find(m => m.key === chartMode);
  const rawHistory = historyFor[chartMode] || [];
  const limitedHistory = chartLimit === "all" ? rawHistory : rawHistory.slice(-chartLimit);
  const chartData = limitedHistory.map((entry, i) => ({
    index: i + 1,
    elo:   entry[mode.eloKey],
    date:  fmtChartDate(entry[mode.dateKey]),
    tier:  eloToTier(entry[mode.eloKey]),
    rawDate: entry[mode.dateKey],
  }));
  const chartColor = mode.color;

  // ELO deltas (last session change per mode)
  const deltas = {
    ranked:   eloDelta(rankedHist.map(e => ({ elo: e.elo_after_match }))),
    vocab:    eloDelta(vocabHist),
    synonyms: eloDelta(synonymsHist),
    antonyms: eloDelta(antonymsHist),
  };

  // ELO card definitions
  const eloCards = [
    {
      key: "ranked",
      label: "Ranked",
      icon: <Trophy size={20} />,
      accentClass: "sp2-card--ranked",
      elo: rankedElo?.elo,
      highest: rankedElo?.highest_elo,
      extra: rankedElo?.matches_played != null ? `${rankedElo.matches_played} matches` : null,
      delta: deltas.ranked,
      histLen: rankedHist.length,
    },
    {
      key: "vocab",
      label: "Vocab",
      icon: <BarChart2 size={20} />,
      accentClass: "sp2-card--vocab",
      elo: vocabElo?.elo,
      highest: vocabElo?.highest_elo,
      delta: deltas.vocab,
      histLen: vocabHist.length,
    },
    {
      key: "synonyms",
      label: "Synonyms",
      icon: <Repeat2 size={20} />,
      accentClass: "sp2-card--synonyms",
      elo: synonymsElo?.elo,
      highest: synonymsElo?.highest_elo,
      delta: deltas.synonyms,
      histLen: synonymsHist.length,
    },
    {
      key: "antonyms",
      label: "Antonyms",
      icon: <ArrowLeftRight size={20} />,
      accentClass: "sp2-card--antonyms",
      elo: antonymsElo?.elo,
      highest: antonymsElo?.highest_elo,
      delta: deltas.antonyms,
      histLen: antonymsHist.length,
    },
  ];

  // ── Loading / error screens ──────────────────────────────────────────────────
  if (loading) return (
    <div className="sp2-fullscreen-center">
      <div className="sp2-loading-card">
        <Loader className="sp2-spinner" size={40} />
        <p>Loading your statistics…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="sp2-fullscreen-center">
      <div className="sp2-loading-card">
        <p className="sp2-err">{error}</p>
        <button className="sp2-btn sp2-btn--primary" onClick={fetchAll}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    </div>
  );

  return (
    <div className="sp2-page">

      {/* ── HEADER ── */}
      <header className="sp2-header">
        <div className="sp2-header-left">
          <TrendingUp size={22} className="sp2-header-icon" />
          <div>
            <h1 className="sp2-title">Statistics</h1>
            <p className="sp2-subtitle">Your complete performance overview</p>
          </div>
        </div>
        <div className="sp2-header-right">
          <Link to="/my-report" className="sp2-btn sp2-btn--outline">
            <ClipboardList size={15} /> My Report
          </Link>
          <button className="sp2-btn sp2-btn--icon" onClick={fetchAll} title="Refresh">
            <RefreshCw size={15} />
          </button>
        </div>
      </header>

      {/* ── SUMMARY STRIP ── */}
      <div className="sp2-summary-strip">
        {xpData && (
          <div className="sp2-summary-item">
            <Zap size={16} className="sp2-summary-icon sp2-summary-icon--xp" />
            <XpBar
              level={xpData.level}
              xp={xpData.xp}
              progressPct={xpData.progressPct}
              xpToNext={xpData.xpToNext}
            />
          </div>
        )}
        {coins !== null && (
          <div className="sp2-summary-item sp2-summary-item--pill">
            <Coins size={15} className="sp2-summary-icon sp2-summary-icon--coins" />
            <span className="sp2-summary-val">{Number(coins).toLocaleString()}</span>
            <span className="sp2-summary-lbl">coins</span>
          </div>
        )}
        {streak !== null && (
          <div className="sp2-summary-item sp2-summary-item--pill">
            <Flame size={15} className="sp2-summary-icon sp2-summary-icon--streak" />
            <span className="sp2-summary-val">{streak}</span>
            <span className="sp2-summary-lbl">day streak</span>
          </div>
        )}
      </div>

      <div className="sp2-body">

        {/* ══ ELO CARDS ══ */}
        <section className="sp2-section">
          <h2 className="sp2-section-title"><Star size={16} /> Current ELO Ratings</h2>
          <div className="sp2-elo-grid">
            {eloCards.map((c) => {
              const tier = c.elo ? eloToTier(c.elo) : 1;
              const { name: tierName, color: tierColor } = tierInfo(tier);
              return (
                <div
                  key={c.key}
                  className={`sp2-elo-card ${c.accentClass}`}
                  onClick={() => setChartMode(c.key)}
                  title="Click to view ELO chart"
                >
                  <div className="sp2-elo-card-top">
                    <div className="sp2-elo-icon">{c.icon}</div>
                    <DeltaBadge delta={c.delta} />
                  </div>
                  <p className="sp2-elo-label">{c.label}</p>
                  <p className="sp2-elo-value">{c.elo ?? "—"}</p>
                  <span className="sp2-tier-badge" style={{ background: tierColor + "22", color: tierColor, border: `1px solid ${tierColor}55` }}>
                    {tierName}
                  </span>
                  <div className="sp2-elo-meta-row">
                    {c.highest != null && <span className="sp2-elo-meta">Best: {c.highest}</span>}
                    {c.extra && <span className="sp2-elo-meta">{c.extra}</span>}
                    <span className="sp2-elo-meta">{c.histLen} sessions</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ══ ELO CHART ══ */}
        <section className="sp2-section">
          <div className="sp2-chart-header">
            <h2 className="sp2-section-title"><TrendingUp size={16} /> ELO Progression</h2>
            <div className="sp2-chart-controls">
              <div className="sp2-tab-group">
                {CHART_MODES.map(m => (
                  <button
                    key={m.key}
                    className={`sp2-tab${chartMode === m.key ? " sp2-tab--active" : ""}`}
                    onClick={() => setChartMode(m.key)}
                    style={chartMode === m.key ? { borderColor: m.color, color: m.color } : {}}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="sp2-tab-group">
                {[20, 50, "all"].map(l => (
                  <button
                    key={l}
                    className={`sp2-tab${chartLimit === l ? " sp2-tab--active" : ""}`}
                    onClick={() => setChartLimit(l)}
                  >
                    {l === "all" ? "All" : `Last ${l}`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="sp2-chart-card">
            {chartData.length === 0 ? (
              <div className="sp2-empty">
                <BookOpen size={32} className="sp2-empty-icon" />
                <p>No {mode.label} history yet.</p>
                <p className="sp2-empty-sub">Play some {mode.label} games to see your ELO trend here.</p>
                <Link to="/selectGameMode" className="sp2-btn sp2-btn--primary sp2-empty-cta">
                  Play now →
                </Link>
              </div>
            ) : (
              <>
                <div className="sp2-chart-summary">
                  {chartData.length >= 2 && (() => {
                    const first = chartData[0].elo;
                    const last  = chartData[chartData.length - 1].elo;
                    const diff  = last - first;
                    return (
                      <span className={`sp2-chart-trend ${diff >= 0 ? "sp2-chart-trend--up" : "sp2-chart-trend--down"}`}>
                        {diff >= 0 ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
                        {Math.abs(diff)} over {chartData.length} sessions
                      </span>
                    );
                  })()}
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={chartData} margin={{ top: 8, right: 20, left: 0, bottom: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "#9ca3af" }}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={false}
                      domain={["auto", "auto"]}
                      width={40}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    {/* Reference lines at tier boundaries visible in current range */}
                    {[100,200,300,400,500,600,700,800,900,1000,1200,1400,1600,1800,2000].map(boundary => (
                      <ReferenceLine key={boundary} y={boundary} stroke="#e5e7eb" strokeDasharray="4 4" />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="elo"
                      stroke={chartColor}
                      strokeWidth={2.5}
                      dot={chartData.length <= 30 ? { fill: chartColor, r: 3, strokeWidth: 0 } : false}
                      activeDot={{ r: 5, fill: chartColor }}
                      name="ELO"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </div>
        </section>

        {/* ══ GAME MODE PERFORMANCE ══ */}
        <section className="sp2-section">
          <h2 className="sp2-section-title"><BarChart2 size={16} /> Game Mode Performance</h2>
          {modeAverages.length === 0 ? (
            <div className="sp2-empty sp2-empty--bordered">
              <BookOpen size={28} className="sp2-empty-icon" />
              <p>No game data yet.</p>
              <p className="sp2-empty-sub">Complete some sessions to see averages here.</p>
              <Link to="/selectGameMode" className="sp2-btn sp2-btn--primary sp2-empty-cta">
                Play now →
              </Link>
            </div>
          ) : (
            <div className="sp2-mode-grid">
              {modeAverages.map(m => {
                const avg = Number(m.avg_score) || 0;
                const pct = Math.min(100, Math.round(avg));
                const barColor = pct >= 75 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
                return (
                  <div key={m.mode} className="sp2-mode-card">
                    <p className="sp2-mode-name">{gameLabel(m.mode)}</p>
                    <p className="sp2-mode-avg">{avg.toFixed(1)}<span className="sp2-mode-unit">%</span></p>
                    <div className="sp2-mode-bar-track">
                      <div className="sp2-mode-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                    </div>
                    <p className="sp2-mode-games">{Number(m.games).toLocaleString()} games</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ══ WEAK WORDS ══ */}
        <section className="sp2-section">
          <h2 className="sp2-section-title"><AlertTriangle size={16} /> Words to Practise</h2>
          {weakWords.length === 0 ? (
            <div className="sp2-empty sp2-empty--bordered">
              <Star size={28} className="sp2-empty-icon sp2-empty-icon--gold" />
              <p>No weak words yet!</p>
              <p className="sp2-empty-sub">Words you attempt 3+ times will appear here with your accuracy rate.</p>
            </div>
          ) : (
            <div className="sp2-weak-grid">
              {weakWords.map(w => {
                const wordEntry = vocabMap?.get(w.word_id);
                const pct = Number(w.pct) || 0;
                const barColor = pct >= 60 ? "#f59e0b" : "#ef4444";
                return (
                  <div key={w.word_id} className="sp2-weak-card">
                    <div className="sp2-weak-top">
                      <span className="sp2-weak-word">
                        {wordEntry ? wordEntry.word : `Word #${w.word_id}`}
                      </span>
                      <span className="sp2-weak-pct" style={{ color: barColor }}>{pct}%</span>
                    </div>
                    {wordEntry?.definition && (
                      <p className="sp2-weak-def">{wordEntry.definition}</p>
                    )}
                    <div className="sp2-weak-bar-track">
                      <div className="sp2-weak-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                    </div>
                    <p className="sp2-weak-meta">{w.correct_count}/{w.attempt_count} correct</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

      </div>
    </div>
  );
}
